import React, { useState, useEffect, useCallback } from 'react';
import { openCertificate } from '@/lib/certificates';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft, Briefcase, GraduationCap, User, Calendar, Clock, MapPin,
  Video, CheckCircle2, Send, Star, MessageSquare, ExternalLink, Loader2,
  Link2, FileText, BookOpen, AlertCircle, ThumbsUp, ThumbsDown, Download, RefreshCw, XCircle,
  RotateCcw, Ban, Phone, Info, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import ConsentPrompt from '@/components/ConsentPrompt';
import { neutralStatusLabel, isWorkplaceMode, modeLabelFor, statusTone, experienceDisplay } from '@/lib/experienceStatus';
import { SessionAgenda, showAgendaFor } from '@/components/SessionAgenda';
import VisitDetailsCard from '@/components/VisitDetailsCard';

const SL = {
  requested: 'Requested', pending_coordinator_action: 'Pending Coordinator',
  pending_parent_confirmation: 'Awaiting Parent', confirmed: 'Confirmed',
  awaiting_feedback: 'Awaiting Feedback', completed: 'Completed', rejected: 'Rejected',
  cancelled: 'Cancelled',
};

function TimelineStep({ label, done, active, last }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex flex-col items-center">
        <div className={`w-3 h-3 rounded-full shrink-0 ${done ? 'bg-[#F97D6A]' : active ? 'ring-2 ring-[#F97D6A] bg-white' : 'bg-gray-200'}`} />
        {!last && <div className={`w-0.5 h-6 ${done ? 'bg-[#F97D6A]' : 'bg-gray-200'}`} />}
      </div>
      <p className={`text-xs -mt-0.5 ${done ? 'text-[#1F2937] font-semibold' : active ? 'text-[#F97D6A] font-semibold' : 'text-[#B0B5C0]'}`}>{label}</p>
    </div>
  );
}

export default function SessionDetailPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const inWorkspace = user?.role === 'parent';
  const pageWrapperClass = inWorkspace ? '' : 'py-6 sm:py-8 ce-section-bg min-h-[calc(100vh-64px)]';
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Complete form
  const [showComplete, setShowComplete] = useState(false);
  const [cNotes, setCNotes] = useState('');
  const [cLearnings, setCLearnings] = useState('');
  const [cRemarks, setCRemarks] = useState('');
  const [completing, setCompleting] = useState(false);

  // Feedback form
  const [showFeedback, setShowFeedback] = useState(false);
  const [searchParams] = useSearchParams();
  const feedbackRequested = searchParams.get('feedback') === '1';
  useEffect(() => {
    if (feedbackRequested && session?.status === 'awaiting_feedback') setShowFeedback(true);
  }, [feedbackRequested, session]);
  const [fRating, setFRating] = useState(0);
  const [fHelpful, setFHelpful] = useState(null);
  const [fComment, setFComment] = useState('');
  const [submittingFb, setSubmittingFb] = useState(false);

  // Session link
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkValue, setLinkValue] = useState('');
  const [savingLink, setSavingLink] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [consentAsk, setConsentAsk] = useState(null);
  const [declining, setDeclining] = useState(false);

  // Parent cancellation + reschedule request
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [showDeclineChoice, setShowDeclineChoice] = useState(false);
  const [declinedSlot, setDeclinedSlot] = useState(false);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [requestingReschedule, setRequestingReschedule] = useState(false);

  const fetchSession = useCallback(async () => {
    try {
      const r = await api.get(`/bookings/${bookingId}/detail`);
      setSession(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Session not found');
      navigate(-1);
    } finally { setLoading(false); }
  }, [bookingId, navigate]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  const isPast = (d) => d && d <= new Date().toISOString().split('T')[0];

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await api.put(`/bookings/${bookingId}/complete`, { session_notes: cNotes || null, key_learnings: cLearnings || null, mentor_remarks: cRemarks || null });
      toast.success('Session completed — awaiting parent feedback');
      setShowComplete(false);
      fetchSession();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setCompleting(false);
  };

  const handleFeedback = async () => {
    if (!fRating || fHelpful === null) { toast.error('Please rate and answer helpfulness'); return; }
    setSubmittingFb(true);
    try {
      await api.put(`/bookings/${bookingId}/feedback`, { rating: fRating, helpful: fHelpful, comment: fComment || null });
      toast.success('Feedback submitted — thank you!');
      setShowFeedback(false);
      fetchSession();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSubmittingFb(false);
  };

  const handleSaveLink = async () => {
    if (!linkValue.trim()) { toast.error('Enter a link'); return; }
    setSavingLink(true);
    try {
      await api.put(`/bookings/${bookingId}/session-link`, { session_link: linkValue });
      toast.success('Session link saved');
      setShowLinkDialog(false);
      fetchSession();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSavingLink(false);
  };

  const handleRegenCert = async () => {
    setRegenerating(true);
    try {
      await api.post(`/bookings/${bookingId}/regenerate-certificate`);
      toast.success('Certificate reissued');
      setConfirmRegen(false);
      fetchSession();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setRegenerating(false);
  };

  const handleDownloadCert = async () => {
    setDownloading(true);
    try {
      await openCertificate(session.certificate_url,
        `certificate-${session.career_name?.replace(/\s+/g, '-')}.pdf`);
    } catch {
      toast.error('Certificate file not available. Ask your Career Coordinator to reissue it.');
    }
    setDownloading(false);
  };

  const handleConfirmSession = async () => {
    setConfirming(true);
    try {
      await api.put(`/bookings/${bookingId}/confirm`);
      toast.success('Session confirmed!');
      fetchSession();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.consent_required) {
        const [doc, visit] = await Promise.all([
          api.get(`/consent/documents/${d.consent_required}`),
          api.get(`/parent/visit-details/${bookingId}`).catch(() => ({ data: null })),
        ]);
        setConsentAsk({ doc: doc.data, reason: d.reason, visit: visit.data });
      } else {
        toast.error(typeof d === 'string' ? d : 'Failed to confirm');
      }
    }
    setConfirming(false);
  };

  const acceptVisitConsent = async () => {
    setConfirming(true);
    try {
      await api.post('/parent/permissions/accept', {
        document_id: consentAsk.doc.id,
        child_id: session?.child_id,
        booking_id: bookingId,
      });
      setConsentAsk(null);
      setConfirming(false);
      await handleConfirmSession();
      return;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not record the permission');
    }
    setConfirming(false);
  };

  const handleDeclineSession = async () => {
    setDeclining(true);
    try {
      await api.put(`/bookings/${bookingId}/decline`);
      toast.success('Session declined');
      fetchSession();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to decline'); }
    setDeclining(false);
  };

  const API_BASE = '';

  const handleCancelExperience = async () => {
    setCancelling(true);
    try {
      await api.put(`/bookings/${bookingId}/cancel`, { reason: cancelReason.trim() || null });
      toast.success('Experience cancelled — the mentor and coordinator have been notified');
      setShowCancel(false);
      fetchSession();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not cancel this experience'); }
    setCancelling(false);
  };

  const handleRequestReschedule = async () => {
    if (!rescheduleReason.trim()) { toast.error('Tell your coordinator why this time does not work'); return; }
    setRequestingReschedule(true);
    try {
      const path = declinedSlot ? 'request-another-time' : 'reschedule-request';
      await api.post(`/bookings/${bookingId}/${path}`, { reason: rescheduleReason.trim() });
      toast.success('Request sent — your school is finding a new time');
      setShowReschedule(false);
      fetchSession();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not send the request'); }
    setRequestingReschedule(false);
  };

  if (loading) return (
    <div className={pageWrapperClass}><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="h-8 w-32 bg-gray-200 rounded mb-6 animate-pulse" /><div className="ce-card p-8 animate-pulse"><div className="h-6 w-48 bg-gray-100 rounded mb-4" /><div className="h-4 w-72 bg-gray-100 rounded" /></div>
    </div></div>
  );
  if (!session) return null;

  const s = session;
  const canComplete = s.status === 'confirmed' && isPast(s.assigned_date) && (s.viewer_is_mentor || s.viewer_role === 'admin');
  const canFeedback = (s.status === 'awaiting_feedback' || (s.status === 'completed' && !s.feedback_rating)) && s.viewer_is_parent;
  const canEditLink = (s.viewer_is_mentor || s.viewer_role === 'coordinator' || s.viewer_role === 'admin') && ['confirmed', 'pending_parent_confirmation'].includes(s.status);
  const isOnline = s.requested_mode === 'online' || s.assigned_mode === 'online';
  const isPendingParent = s.status === 'pending_parent_confirmation' && s.viewer_is_parent;
  const isOffline = isWorkplaceMode(s.assigned_mode || s.requested_mode);
  const canManageSchedule = s.viewer_is_parent && ['confirmed', 'pending_parent_confirmation', 'awaiting_reschedule'].includes(s.status);
  const pendingReschedule = s.reschedule_request?.status === 'pending';
  const venue = s.assigned_venue || {};
  const awaitingNewTime = ['awaiting_reschedule', 'waitlisted'].includes(s.status);
  const history = s.reschedule_history || [];
  const lastMove = s.status === 'pending_parent_confirmation' && history.length ? history[history.length - 1] : null;

  const canRegenerateCert = ['coordinator', 'admin'].includes(s.viewer_role);

  // Timeline
  const isCancelled = s.status === 'cancelled';
  const steps = isCancelled ? [
    { label: 'Requested', done: true },
    { label: 'Mentor Matched', done: !!s.mentor_name },
    { label: 'Cancelled', done: true },
  ] : [
    { label: 'Requested', done: true },
    { label: 'Mentor Matched', done: !!s.mentor_name },
    { label: 'Parent Confirmed', done: ['confirmed', 'awaiting_feedback', 'completed'].includes(s.status) },
    { label: 'Session Completed', done: ['awaiting_feedback', 'completed'].includes(s.status) },
    { label: 'Feedback Submitted', done: s.status === 'completed' && !!s.feedback_rating },
    { label: 'Certificates Generated', done: !!s.certificate_url },
  ];
  const activeIdx = isCancelled ? -1 : steps.findIndex(st => !st.done);

  return (
    <>
    <div className={pageWrapperClass} data-testid="session-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-sm text-[#6B7280] hover:text-[#1F2937] mb-5 -ml-2 rounded-lg" data-testid="session-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1.5"/> Back
        </Button>

        {/* Header */}
        <div className="ce-card-elevated p-5 sm:p-6 mb-5" data-testid="session-header">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <p className="text-xs font-bold text-[#B0B5C0] uppercase tracking-wider mb-1">Session</p>
              <h1 className="text-xl sm:text-2xl font-extrabold text-[#1F2937] font-['Outfit'] tracking-tight" data-testid="session-career">{s.career_name}</h1>
              {s.group_size > 1 && (
                <p className="text-xs text-[#6B7280] mt-1.5 inline-flex items-center gap-1.5" data-testid="group-indicator">
                  <Users className="h-3.5 w-3.5 text-[#B0B5C0]" />
                  Group session · {s.group_size} students
                </p>
              )}
            </div>
            <Badge className={`text-[11px] ce-badge uppercase px-3 py-1 rounded-full border-0 ${statusTone(s)}`} data-testid="session-status">{experienceDisplay(s).label}</Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div className="flex items-center gap-2 text-[#6B7280]"><Calendar className="h-3.5 w-3.5 text-[#B0B5C0]"/><span>{awaitingNewTime ? 'New time being arranged' : (formatDate(s.assigned_date) || 'Not scheduled')}</span></div>
            <div className="flex items-center gap-2 text-[#6B7280]"><Clock className="h-3.5 w-3.5 text-[#B0B5C0]"/><span>{awaitingNewTime ? '—' : (s.assigned_time_slot || '-')}</span></div>
            <div className="flex items-center gap-2 text-[#6B7280]"><MapPin className="h-3.5 w-3.5 text-[#B0B5C0]"/><span>{modeLabelFor(s.assigned_mode || s.requested_mode)}</span></div>
            <div className="flex items-center gap-2 text-[#6B7280]"><Briefcase className="h-3.5 w-3.5 text-[#B0B5C0]"/><span>{s.school_name}</span></div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-5">
          {/* Left Column: Participants + Access */}
          <div className="sm:col-span-2 space-y-5">
            {/* Participants */}
            <div className="ce-card p-5" data-testid="session-participants">
              <h2 className="text-sm font-bold text-[#1F2937] font-['Outfit'] mb-3">Participants</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Child */}
                <div className="bg-[#F7F8FA] rounded-xl p-3">
                  <p className="text-xs font-bold text-[#B0B5C0] uppercase tracking-wider mb-1">Student</p>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-[#FFF0ED] flex items-center justify-center text-sm font-bold text-[#F97D6A]">{s.child_name?.charAt(0)}</div>
                    <div><p className="text-sm font-semibold text-[#1F2937]">{s.child_name}</p><p className="text-[10px] text-[#B0B5C0]">Grade {s.child_grade}{s.child_division ? `-${s.child_division}` : ''}{s.child_admission_number ? ` · #${s.child_admission_number}` : ''}</p></div>
                  </div>
                  <p className="text-[10px] text-[#B0B5C0]">Parent: <span className="text-[#6B7280] font-medium">{s.parent_name}</span></p>
                </div>
                {/* Mentor */}
                <div className="bg-[#F7F8FA] rounded-xl p-3">
                  <p className="text-xs font-bold text-[#B0B5C0] uppercase tracking-wider mb-1">Mentor</p>
                  {s.mentor_name ? (
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-sm font-bold text-emerald-600">{s.mentor_name?.charAt(0)}</div>
                      <div><p className="text-sm font-semibold text-[#1F2937]">{s.mentor_name}</p><p className="text-[10px] text-[#B0B5C0]">{s.mentor_profession}{s.mentor_company ? ` at ${s.mentor_company}` : ''}</p></div>
                    </div>
                  ) : <p className="text-xs text-[#B0B5C0] italic">Not assigned yet</p>}
                </div>
              </div>
            </div>

            {/* Full Mentor Profile — shown for parent approval */}
            {isPendingParent && s.mentor_name && (
              <div className="ce-card p-5 ring-1 ring-violet-200 bg-violet-50/30" data-testid="mentor-full-profile">
                <div className="flex items-center gap-2 mb-4">
                  <User className="h-4 w-4 text-violet-600" />
                  <h2 className="text-sm font-bold text-[#1F2937] font-['Outfit']">Mentor Profile</h2>
                </div>

                {/* Mentor header */}
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center text-lg font-bold text-emerald-600 shrink-0">
                    {s.mentor_name?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-[#1F2937] font-['Outfit']">{s.mentor_name}</p>
                    <p className="text-sm text-[#6B7280]">
                      {s.mentor_profession}{s.mentor_company ? ` at ${s.mentor_company}` : ''}
                    </p>
                    {s.mentor_years_of_experience && (
                      <p className="text-[11px] text-[#9CA3AF] mt-0.5">{s.mentor_years_of_experience} years of experience</p>
                    )}
                  </div>
                  {/* Rating badge */}
                  <div className="shrink-0 text-right">
                    {s.mentor_avg_rating ? (
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                        <span className="text-sm font-bold text-[#1F2937]">{s.mentor_avg_rating}</span>
                        <span className="text-[10px] text-[#9CA3AF]">({s.mentor_rating_count})</span>
                      </div>
                    ) : (
                      <span className="text-[10px] text-[#9CA3AF] italic">No feedback yet</span>
                    )}
                  </div>
                </div>

                {/* Bio */}
                {s.mentor_bio && (
                  <div className="mb-3">
                    <p className="text-xs font-bold text-[#B0B5C0] uppercase tracking-wider mb-1">About</p>
                    <p className="text-sm text-[#4B5563] bg-white rounded-xl p-3 leading-relaxed">{s.mentor_bio}</p>
                  </div>
                )}

                {/* Details grid */}
                <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                  {s.mentor_linkedin_url && (
                    <div>
                      <p className="text-[10px] text-[#B0B5C0] mb-0.5">LinkedIn</p>
                      <a href={s.mentor_linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[#F97D6A] hover:underline truncate block text-xs" data-testid="mentor-linkedin">
                        View Profile
                      </a>
                    </div>
                  )}
                  {s.mentor_session_modes?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-[#B0B5C0] mb-0.5">Session Modes</p>
                      <div className="flex gap-1">{s.mentor_session_modes.map(m => (
                        <span key={m} className="text-[11px] bg-white text-[#4B5563] px-2 py-0.5 rounded">{modeLabelFor(m)}</span>
                      ))}</div>
                    </div>
                  )}
                  {s.mentor_preferred_city && (
                    <div>
                      <p className="text-[10px] text-[#B0B5C0] mb-0.5">Preferred City</p>
                      <p className="text-[#1F2937]">{s.mentor_preferred_city}</p>
                    </div>
                  )}
                </div>

                {/* Career mapping */}
                {s.mentor_mapped_careers?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-bold text-[#B0B5C0] uppercase tracking-wider mb-1">Expertise Areas</p>
                    <div className="flex flex-wrap gap-1.5">
                      {s.mentor_mapped_careers.map(c => (
                        <span key={c.slug} className="text-[11px] font-medium bg-[#FFF0ED] text-[#F97D6A] px-2.5 py-1 rounded-lg">{c.name}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Offline details */}
                {isOffline && s.mentor_offline_capabilities?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-bold text-[#B0B5C0] uppercase tracking-wider mb-1">Workplace Visit Location</p>
                    {s.mentor_offline_capabilities.filter(c => c.career_slug === s.career_slug).map((cap, i) => (
                      <div key={i} className="bg-white rounded-xl p-2.5 text-xs">
                        <p className="font-medium text-[#1F2937]">{cap.workplace_name}</p>
                        <p className="text-[#6B7280]">{cap.address}, {cap.city}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Safety note for offline */}
                {isOffline && (
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 mb-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                      <div className="text-[11px] text-amber-700 leading-relaxed">
                        <p className="font-semibold mb-0.5">Workplace visit</p>
                        <p>
                          Your signed consent covers this visit. {venue.supervised_by === 'workplace_host'
                            ? `${venue.host_name || 'An approved workplace host'} supervises the students throughout.`
                            : 'The approved mentor supervises the students throughout.'}
                          {' '}You handle drop-off and pick-up unless your school has arranged transport.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {s.coordinator_notes && (
                  <div className="bg-white rounded-xl p-2.5 mb-3">
                    <p className="text-xs font-bold text-[#B0B5C0] uppercase tracking-wider mb-0.5">Coordinator Note</p>
                    <p className="text-sm text-[#4B5563] italic">{s.coordinator_notes}</p>
                  </div>
                )}

                {/* Accept / Decline buttons */}
                {lastMove && (
                  <div className="bg-violet-50 border border-violet-100 rounded-xl p-3 mb-3" data-testid="revised-schedule-note">
                    <p className="text-[11px] text-violet-700 leading-relaxed">
                      <span className="font-semibold">Revised schedule.</span> Moved from {formatDate(lastMove.from_date)}
                      {lastMove.from_time_slot ? ` at ${lastMove.from_time_slot}` : ''} at
                      {lastMove.initiated_by === 'parent' ? ' your request' : ' your coordinator\'s request'}.
                      If this time does not work, you can ask for another one without losing the experience.
                    </p>
                  </div>
                )}
                <div className="flex items-center gap-3 pt-3 border-t border-violet-100">
                  <Button
                    onClick={handleConfirmSession}
                    disabled={confirming || declining}
                    className="bg-emerald-500 text-white hover:bg-emerald-600 rounded-xl h-10 font-semibold text-sm flex-1"
                    data-testid="confirm-booking-btn"
                  >
                    {confirming ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <CheckCircle2 className="h-4 w-4 mr-2"/>}
                    {confirming ? 'Confirming...' : 'Accept Session'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => (lastMove ? setShowDeclineChoice(true) : handleDeclineSession())}
                    disabled={confirming || declining}
                    className="border-red-200 text-red-500 hover:bg-red-50 rounded-xl h-10 font-semibold text-sm flex-1"
                    data-testid="decline-booking-btn"
                  >
                    {declining ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <XCircle className="h-4 w-4 mr-2"/>}
                    {declining ? 'Declining...' : 'Decline'}
                  </Button>
                </div>
              </div>
            )}

            {/* Session Access */}
            {isOnline && ['confirmed', 'pending_parent_confirmation', 'awaiting_feedback', 'completed'].includes(s.status) && (
              <div className="ce-card p-5" data-testid="session-access">
                <h2 className="text-sm font-bold text-[#1F2937] font-['Outfit'] mb-3">Session Access</h2>
                {(s.google_meet_link || s.session_link) ? (
                  <div className="flex items-center gap-3 bg-blue-50/60 rounded-xl p-3">
                    <Video className="h-5 w-5 text-blue-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-blue-800">
                        {s.google_meet_link ? 'Google Meet' : 'Meeting Link'}
                      </p>
                      <a href={s.google_meet_link || s.session_link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline truncate block" data-testid="session-join-link">{s.google_meet_link || s.session_link}</a>
                    </div>
                    <a href={s.google_meet_link || s.session_link} target="_blank" rel="noopener noreferrer"><Button size="sm" className="ce-btn-primary bg-blue-600 text-white hover:bg-blue-700 rounded-lg h-8 text-xs px-4 font-semibold shrink-0" data-testid="join-session-btn"><ExternalLink className="h-3.5 w-3.5 mr-1"/>Join</Button></a>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 bg-[#F7F8FA] rounded-xl p-3">
                    <Link2 className="h-5 w-5 text-[#B0B5C0]" />
                    <p className="text-xs text-[#B0B5C0] flex-1" data-testid="meet-pending-note">
                      {s.google_sync_status === 'pending' || s.google_sync_status === 'failed'
                        ? 'Google Meet link is being prepared. It will appear here shortly.'
                        : 'Meeting link to be shared'}
                    </p>
                    {canEditLink && <Button size="sm" variant="outline" onClick={() => { setLinkValue(s.session_link || ''); setShowLinkDialog(true); }} className="rounded-lg h-7 text-[11px] px-3 border-gray-200" data-testid="add-link-btn"><Link2 className="h-3 w-3 mr-1"/>Add Link</Button>}
                  </div>
                )}
                {s.assigned_end_time && (
                  <p className="text-[11px] text-[#9CA3AF] mt-2" data-testid="session-window">
                    {s.assigned_time_slot}–{s.assigned_end_time}
                    {s.assigned_duration_minutes ? ` · ${s.assigned_duration_minutes} minutes` : ''}
                    {s.timezone ? ` · ${s.timezone}` : ''}
                  </p>
                )}
                {s.session_link && canEditLink && (
                  <Button size="sm" variant="ghost" onClick={() => { setLinkValue(s.session_link || ''); setShowLinkDialog(true); }} className="mt-2 text-sm text-[#6B7280] hover:text-[#F97D6A]" data-testid="edit-link-btn"><Link2 className="h-3 w-3 mr-1"/>Edit Link</Button>
                )}
              </div>
            )}

            {/* Sample session agenda (online only) */}
            {isOnline && showAgendaFor(s.status) && <SessionAgenda />}

            {/* Workplace visit details */}
            {isOffline && ['confirmed', 'pending_parent_confirmation', 'awaiting_feedback', 'completed'].includes(s.status) && (
              <VisitDetailsCard venue={venue} mentorName={s.mentor_name} />
            )}

            {/* Notes / Learnings */}
            {(s.session_notes || s.key_learnings || s.mentor_remarks) && (
              <div className="ce-card p-5" data-testid="session-notes">
                <h2 className="text-sm font-bold text-[#1F2937] font-['Outfit'] mb-3">Mentor Notes & Learnings</h2>
                {s.session_notes && <div className="mb-3"><p className="text-xs font-bold text-[#B0B5C0] uppercase mb-1">Session Notes</p><p className="text-sm text-[#4B5563] bg-[#F7F8FA] rounded-xl p-3 leading-relaxed whitespace-pre-wrap">{s.session_notes}</p></div>}
                {s.key_learnings && <div className="mb-3"><p className="text-xs font-bold text-[#B0B5C0] uppercase mb-1">Key Learnings</p><p className="text-sm text-[#4B5563] bg-[#F7F8FA] rounded-xl p-3 leading-relaxed whitespace-pre-wrap">{s.key_learnings}</p></div>}
                {s.mentor_remarks && <div><p className="text-xs font-bold text-[#B0B5C0] uppercase mb-1">Remarks</p><p className="text-sm text-[#4B5563] bg-[#F7F8FA] rounded-xl p-3 leading-relaxed whitespace-pre-wrap">{s.mentor_remarks}</p></div>}
              </div>
            )}

            {/* Feedback */}
            {s.feedback_rating && (
              <div className="ce-card p-5" data-testid="session-feedback">
                <h2 className="text-sm font-bold text-[#1F2937] font-['Outfit'] mb-3">Parent Feedback</h2>
                <div className="flex items-center gap-4 mb-3">
                  <div className="flex gap-0.5">{[1,2,3,4,5].map(i => <Star key={i} className={`h-5 w-5 ${i <= s.feedback_rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`}/>)}</div>
                  <Badge className={`text-[10px] font-semibold rounded-full px-2.5 py-0.5 border-0 ${s.feedback_helpful ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                    {s.feedback_helpful ? 'Helpful' : 'Not helpful'}
                  </Badge>
                </div>
                {s.feedback_comment && <p className="text-sm text-[#4B5563] bg-[#F7F8FA] rounded-xl p-3 leading-relaxed">{s.feedback_comment}</p>}
                {s.feedback_submitted_at && <p className="text-[10px] text-[#B0B5C0] mt-2">Submitted {formatDate(s.feedback_submitted_at)}</p>}
              </div>
            )}
          </div>

          {/* Right Column: Timeline + Actions */}
          <div className="space-y-5">
            {/* Timeline */}
            <div className="ce-card p-5" data-testid="session-timeline">
              <h2 className="text-sm font-bold text-[#1F2937] font-['Outfit'] mb-4">Timeline</h2>
              <div>{steps.map((step, i) => <TimelineStep key={i} label={step.label} done={step.done} active={i === activeIdx} last={i === steps.length - 1}/>)}</div>
              {s.created_at && <p className="text-[10px] text-[#B0B5C0] mt-3">Requested {formatDate(s.created_at)}</p>}
              {s.completed_at && <p className="text-[10px] text-[#B0B5C0]">Completed {formatDate(s.completed_at)}</p>}
            </div>

            {/* Actions */}
            <div className="space-y-2">
              {canComplete && (
                <Button onClick={() => setShowComplete(true)} className="ce-btn-primary bg-teal-500 text-white hover:bg-teal-600 rounded-xl h-10 w-full font-semibold text-sm" data-testid="mark-complete-btn">
                  <CheckCircle2 className="h-4 w-4 mr-2"/>Mark as Completed
                </Button>
              )}
              {canFeedback && (
                <Button onClick={() => setShowFeedback(true)} className="ce-btn-primary bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-10 w-full font-semibold text-sm" data-testid="give-feedback-btn">
                  <Star className="h-4 w-4 mr-2"/>Give Feedback
                </Button>
              )}
              {s.status === 'awaiting_feedback' && !s.viewer_is_parent && (
                <div className="bg-amber-50 rounded-xl p-3 text-center">
                  <p className="text-xs text-amber-700 font-medium">Awaiting parent feedback</p>
                </div>
              )}
              {pendingReschedule && (
                <div className="bg-violet-50 rounded-xl p-3" data-testid="reschedule-pending-note">
                  <p className="text-xs text-violet-700 leading-relaxed">
                    {s.status === 'awaiting_reschedule'
                      ? `You declined ${formatDate(s.reschedule_request.from_date)}${s.reschedule_request.from_time_slot ? ` at ${s.reschedule_request.from_time_slot}` : ''}. Your school is finding a new time and your coordinator will confirm it with you — the experience is still active.`
                      : 'Your school is finding a new time. Your coordinator will confirm it with you before anything changes.'}
                  </p>
                  <p className="text-xs text-violet-700 mt-1.5" data-testid="reschedule-reason-note">
                    Your reason: "{s.reschedule_request.reason}"
                  </p>
                </div>
              )}
              {s.reschedule_request?.status === 'declined' && (
                <div className="bg-[#F7F8FA] rounded-xl p-3" data-testid="reschedule-declined-note">
                  <p className="text-sm text-[#6B7280] leading-relaxed">
                    Your coordinator kept the original schedule
                    {s.reschedule_request.coordinator_note ? `: "${s.reschedule_request.coordinator_note}"` : '.'}
                  </p>
                </div>
              )}
              {canManageSchedule && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => { setDeclinedSlot(false); setRescheduleReason(''); setShowReschedule(true); }}
                    disabled={pendingReschedule}
                    className="rounded-xl h-10 w-full font-semibold text-sm border-gray-200 text-[#4B5563] disabled:opacity-40"
                    data-testid="request-reschedule-btn"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />{pendingReschedule ? 'Reschedule Requested' : 'Request Reschedule'}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => { setCancelReason(''); setShowCancel(true); }}
                    className="rounded-xl h-10 w-full font-semibold text-sm text-red-500 hover:bg-red-50 hover:text-red-600"
                    data-testid="cancel-experience-btn"
                  >
                    <Ban className="h-4 w-4 mr-2" />Cancel Experience
                  </Button>
                </>
              )}
              {s.status === 'cancelled' && (
                <div className="bg-red-50 rounded-xl p-3" data-testid="cancelled-note">
                  <p className="text-xs text-red-600 leading-relaxed">
                    Cancelled{s.cancelled_by ? ` by ${s.cancelled_by}` : ''}
                    {s.cancellation_reason ? ` — "${s.cancellation_reason}"` : ''}. The seat has been released.
                  </p>
                </div>
              )}
            </div>

            {/* Certificate */}
            {s.status === 'completed' && s.feedback_rating && (
              <div className="ce-card p-5" data-testid="session-certificate">
                <h2 className="text-sm font-bold text-[#1F2937] font-['Outfit'] mb-3">Certificate</h2>
                {s.certificate_url ? (
                  <div className="space-y-2">
                    <Button onClick={handleDownloadCert} disabled={downloading} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-10 w-full font-semibold text-xs sm:text-sm px-3" data-testid="download-certificate-btn">
                      {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin shrink-0"/> : <Download className="h-4 w-4 mr-2 shrink-0"/>}
                      <span className="truncate">{downloading ? 'Downloading...' : 'Download Certificate'}</span>
                    </Button>
                    {canRegenerateCert && (
                      <Button variant="ghost" size="sm" onClick={() => setConfirmRegen(true)} disabled={regenerating} className="w-full text-sm text-[#6B7280] hover:text-[#F97D6A]" data-testid="regenerate-certificate-btn">
                        {regenerating ? <Loader2 className="h-3 w-3 mr-1 animate-spin"/> : <RefreshCw className="h-3 w-3 mr-1"/>}
                        Reissue certificate
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-center space-y-2">
                    <p className="text-xs text-[#9CA3AF]">Certificate is being generated...</p>
                    {canRegenerateCert && (
                      <Button variant="outline" size="sm" onClick={() => setConfirmRegen(true)} disabled={regenerating} className="rounded-lg text-xs" data-testid="generate-certificate-btn">
                        {regenerating ? <Loader2 className="h-3 w-3 mr-1 animate-spin"/> : <RefreshCw className="h-3 w-3 mr-1"/>}
                        Generate Now
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Coordinator Notes */}
            {s.coordinator_notes && (
              <div className="ce-card p-4">
                <p className="text-xs font-bold text-[#B0B5C0] uppercase tracking-wider mb-1">Coordinator Notes</p>
                <p className="text-sm text-[#4B5563] italic">{s.coordinator_notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Complete Dialog */}
        <Dialog open={showComplete} onOpenChange={setShowComplete}>
          <DialogContent className="rounded-2xl sm:max-w-md border-0 shadow-[var(--shadow-overlay)]" data-testid="complete-dialog">
            <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1F2937] text-lg">Complete Session</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-[#6B7280]">Share notes and learnings from your session with <strong>{s.child_name}</strong> on <strong>{s.career_name}</strong>.</p>
              <div><p className="text-xs font-semibold text-[#1F2937] mb-1.5">Session Notes</p><Textarea value={cNotes} onChange={e => setCNotes(e.target.value)} placeholder="What was covered in the session..." className="rounded-xl border-0 shadow-[var(--shadow-xs)] bg-[#F7F8FA] min-h-[80px] text-sm" data-testid="complete-notes"/></div>
              <div><p className="text-xs font-semibold text-[#1F2937] mb-1.5">Key Learnings</p><Textarea value={cLearnings} onChange={e => setCLearnings(e.target.value)} placeholder="Main takeaways for the student..." className="rounded-xl border-0 shadow-[var(--shadow-xs)] bg-[#F7F8FA] min-h-[60px] text-sm" data-testid="complete-learnings"/></div>
              <div><p className="text-xs font-semibold text-[#1F2937] mb-1.5">Remarks (optional)</p><Input value={cRemarks} onChange={e => setCRemarks(e.target.value)} placeholder="Any additional comments..." className="rounded-xl border-0 shadow-[var(--shadow-xs)] bg-[#F7F8FA] h-10 text-sm" data-testid="complete-remarks"/></div>
            </div>
            <DialogFooter className="gap-2 mt-2">
              <Button variant="outline" onClick={() => setShowComplete(false)} className="rounded-xl border-gray-200">Cancel</Button>
              <Button onClick={handleComplete} disabled={completing} className="ce-btn-primary bg-teal-500 text-white hover:bg-teal-600 rounded-xl font-semibold" data-testid="submit-complete">
                {completing ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/>Saving...</> : <><CheckCircle2 className="h-4 w-4 mr-1.5"/>Complete Session</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Feedback Dialog */}
        <Dialog open={showFeedback} onOpenChange={setShowFeedback}>
          <DialogContent className="rounded-2xl sm:max-w-md border-0 shadow-[var(--shadow-overlay)]" data-testid="feedback-dialog">
            <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1F2937] text-lg">Session Feedback</DialogTitle></DialogHeader>
            <div className="space-y-5">
              <p className="text-sm text-[#6B7280]">How was <strong>{s.child_name}</strong>'s session with <strong>{s.mentor_name}</strong>?</p>
              <div>
                <p className="text-xs font-semibold text-[#1F2937] mb-2">Rating</p>
                <div className="flex gap-1" data-testid="feedback-stars">
                  {[1,2,3,4,5].map(i => (
                    <button key={i} onClick={() => setFRating(i)} className="p-0.5 transition-transform duration-100 hover:scale-110">
                      <Star className={`h-7 w-7 transition-colors duration-100 ${i <= fRating ? 'text-amber-400 fill-amber-400' : 'text-gray-200 hover:text-amber-200'}`}/>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#1F2937] mb-2">Was the session helpful?</p>
                <div className="flex gap-2" data-testid="feedback-helpful">
                  <button onClick={() => setFHelpful(true)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${fHelpful === true ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 shadow-sm' : 'bg-white text-[#6B7280] shadow-[var(--shadow-xs)] hover:shadow-sm'}`}><ThumbsUp className="h-4 w-4"/>Yes</button>
                  <button onClick={() => setFHelpful(false)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${fHelpful === false ? 'bg-red-50 text-red-600 ring-1 ring-red-200 shadow-sm' : 'bg-white text-[#6B7280] shadow-[var(--shadow-xs)] hover:shadow-sm'}`}><ThumbsDown className="h-4 w-4"/>No</button>
                </div>
              </div>
              <div><p className="text-xs font-semibold text-[#1F2937] mb-1.5">Comment (optional)</p><Textarea value={fComment} onChange={e => setFComment(e.target.value)} placeholder="Share your thoughts..." className="rounded-xl border-0 shadow-[var(--shadow-xs)] bg-[#F7F8FA] min-h-[60px] text-sm" data-testid="feedback-comment"/></div>
              <p className="text-[11px] text-[#6B7280] bg-[#F7F8FA] rounded-xl px-3 py-2.5 leading-relaxed" data-testid="feedback-visibility-notice">
                Your rating and feedback will be visible to the mentor, along with your name and your child's name.
              </p>
            </div>
            <DialogFooter className="gap-2 mt-2">
              <Button variant="outline" onClick={() => setShowFeedback(false)} className="rounded-xl border-gray-200">Cancel</Button>
              <Button onClick={handleFeedback} disabled={submittingFb || !fRating || fHelpful === null} className="ce-btn-primary bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold disabled:opacity-40" data-testid="submit-feedback">
                {submittingFb ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/>Submitting...</> : <><Send className="h-4 w-4 mr-1.5"/>Submit Feedback</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Decline a revised schedule: keep the experience or cancel it */}
        <Dialog open={showDeclineChoice} onOpenChange={setShowDeclineChoice}>
          <DialogContent className="rounded-2xl sm:max-w-md border-0 shadow-[var(--shadow-overlay)]" data-testid="decline-choice-dialog">
            <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1F2937] text-lg">This time doesn't work?</DialogTitle></DialogHeader>
            <p className="text-sm text-[#6B7280] leading-relaxed">
              {formatDate(s.assigned_date)}{s.assigned_time_slot ? ` at ${s.assigned_time_slot}` : ''} will not be
              treated as confirmed. Choose what happens to <strong>{s.child_name}</strong>'s {s.career_name} experience.
            </p>
            <div className="space-y-2.5">
              <button
                onClick={() => { setShowDeclineChoice(false); setDeclinedSlot(true); setRescheduleReason(''); setShowReschedule(true); }}
                className="w-full text-left rounded-xl border border-[#E5E7EB] hover:border-[#F97D6A] bg-white p-3.5 transition-colors"
                data-testid="decline-request-another-btn"
              >
                <p className="text-sm font-semibold text-[#1F2937] flex items-center gap-2"><RotateCcw className="h-4 w-4 text-[#F97D6A]"/>Request another time</p>
                <p className="text-[11px] text-[#6B7280] mt-1 leading-relaxed">
                  Keeps the experience. Your coordinator is told you need a different time and will propose a new slot.
                </p>
              </button>
              <button
                onClick={() => { setShowDeclineChoice(false); setCancelReason(''); setShowCancel(true); }}
                className="w-full text-left rounded-xl border border-[#E5E7EB] hover:border-red-200 bg-white p-3.5 transition-colors"
                data-testid="decline-cancel-btn"
              >
                <p className="text-sm font-semibold text-[#1F2937] flex items-center gap-2"><Ban className="h-4 w-4 text-red-500"/>Cancel the experience</p>
                <p className="text-[11px] text-[#6B7280] mt-1 leading-relaxed">
                  Ends this experience, releases the seat and tells the mentor and coordinator.
                </p>
              </button>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowDeclineChoice(false)} className="rounded-xl text-[#6B7280]" data-testid="decline-choice-back-btn">Back</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel Experience Dialog */}
        <Dialog open={showCancel} onOpenChange={setShowCancel}>
          <DialogContent className="rounded-2xl sm:max-w-md border-0 shadow-[var(--shadow-overlay)]" data-testid="cancel-dialog">
            <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1F2937] text-lg">Cancel this experience?</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-[#6B7280] leading-relaxed">
                <strong>{s.child_name}</strong>'s {s.career_name} experience on <strong>{formatDate(s.assigned_date)}</strong>
                {s.assigned_time_slot ? ` at ${s.assigned_time_slot}` : ''} will be cancelled and the seat released.
                {s.mentor_name ? ` ${s.mentor_name} and your coordinator will be notified.` : ''} This cannot be undone —
                you would need to request the experience again.
              </p>
              <div>
                <p className="text-xs font-semibold text-[#1F2937] mb-1.5">Reason (optional)</p>
                <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Helps your coordinator plan ahead" className="rounded-xl border-0 shadow-[var(--shadow-xs)] bg-[#F7F8FA] min-h-[60px] text-sm" data-testid="cancel-reason" />
              </div>
            </div>
            <DialogFooter className="gap-2 mt-2">
              <Button variant="outline" onClick={() => setShowCancel(false)} className="rounded-xl border-gray-200" data-testid="cancel-dismiss-btn">Keep Experience</Button>
              <Button onClick={handleCancelExperience} disabled={cancelling} className="bg-red-500 text-white hover:bg-red-600 rounded-xl font-semibold" data-testid="confirm-cancel-btn">
                {cancelling ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/>Cancelling...</> : <><Ban className="h-4 w-4 mr-1.5"/>Cancel Experience</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reschedule Request Dialog */}
        <Dialog open={showReschedule} onOpenChange={setShowReschedule}>
          <DialogContent className="rounded-2xl sm:max-w-md border-0 shadow-[var(--shadow-overlay)]" data-testid="reschedule-dialog">
            <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1F2937] text-lg">{declinedSlot ? 'Ask for another time' : 'Request a reschedule'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-[#6B7280] leading-relaxed">
                {declinedSlot
                  ? <>The slot on <strong>{formatDate(s.assigned_date)}</strong> is released and your school will find the next suitable time, then confirm it with you.</>
                  : <>Your school will find the next suitable time and confirm it with you — you do not need to pick a date.</>}
              </p>
              <div>
                <p className="text-xs font-semibold text-[#1F2937] mb-1.5">Why does this time not work?</p>
                <Textarea value={rescheduleReason} onChange={e => setRescheduleReason(e.target.value)} placeholder="e.g. school exams that week" className="rounded-xl border-0 shadow-[var(--shadow-xs)] bg-[#F7F8FA] min-h-[60px] text-sm" data-testid="reschedule-reason" />
              </div>
            </div>
            <DialogFooter className="gap-2 mt-2">
              <Button variant="outline" onClick={() => setShowReschedule(false)} className="rounded-xl border-gray-200">Back</Button>
              <Button onClick={handleRequestReschedule} disabled={requestingReschedule || !rescheduleReason.trim()} className="ce-btn-primary bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold disabled:opacity-40" data-testid="submit-reschedule-btn">
                {requestingReschedule ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/>Sending...</> : <><Send className="h-4 w-4 mr-1.5"/>Send Request</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Session Link Dialog */}
        <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
          <DialogContent className="rounded-2xl sm:max-w-sm border-0 shadow-[var(--shadow-overlay)]" data-testid="link-dialog">
            <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1F2937]">Session Link</DialogTitle></DialogHeader>
            <Input value={linkValue} onChange={e => setLinkValue(e.target.value)} placeholder="https://meet.google.com/..." className="rounded-xl border-0 shadow-[var(--shadow-xs)] bg-[#F7F8FA] h-10 text-sm" data-testid="link-input"/>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowLinkDialog(false)} className="rounded-xl border-gray-200">Cancel</Button>
              <Button onClick={handleSaveLink} disabled={savingLink} className="ce-btn-primary bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold" data-testid="save-link-btn">
                {savingLink ? <Loader2 className="h-4 w-4 animate-spin"/> : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reissue certificate — coordinators only */}
        <Dialog open={confirmRegen} onOpenChange={setConfirmRegen}>
          <DialogContent className="rounded-2xl sm:max-w-sm border-0 shadow-[var(--shadow-overlay)]" data-testid="regenerate-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Outfit'] text-[#1F2937]">Reissue this certificate?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[#6B7280] leading-relaxed">
              Use this only to correct the details printed on the certificate — the student's name,
              the career or your school's name and logo. The certificate number stays the same and the
              old file is replaced, so anyone who downloads it again gets the corrected version.
            </p>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setConfirmRegen(false)} className="rounded-xl border-gray-200" data-testid="regenerate-cancel-btn">Cancel</Button>
              <Button onClick={handleRegenCert} disabled={regenerating} className="bg-[#1F2937] text-white hover:bg-[#111827] rounded-xl font-semibold" data-testid="regenerate-confirm-btn">
                {regenerating ? <Loader2 className="h-4 w-4 animate-spin"/> : 'Reissue certificate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </div>
      <ConsentPrompt
        doc={consentAsk?.doc}
        open={!!consentAsk}
        reason={consentAsk?.reason}
        details={consentAsk?.visit ? [
          ['Host', consentAsk.visit.host],
          ['Date', consentAsk.visit.date],
          ['Time', [consentAsk.visit.time, consentAsk.visit.end_time].filter(Boolean).join(' – ')],
          ['Location', consentAsk.visit.location],
          ['Host contact', consentAsk.visit.mentor],
          ['Supervision', consentAsk.visit.supervision],
          ['Transport', consentAsk.visit.transport],
          ['On the day', consentAsk.visit.contact],
        ] : null}
        onOpenChange={() => setConsentAsk(null)}
        onAccept={acceptVisitConsent}
        busy={confirming}
      />
    </>
  );
}
